import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import { Tooltip } from '@mui/material';
import { ArrowBackIos, Save } from '@mui/icons-material';
import { ISportSetting } from '../../../../types/sports/sportsInterfaces';
import {
  useGetSportLocationSettingsMutation,
  useCreateSportLocationSettingsMutation,
  useEditSportLocationSettingsMutation
} from '../../../../services/sportService/sportService';
import { CardSportSetting } from './cardSportSetting/cardSportSetting';
import Strings from '../../../../i18n';
import './sportSettingStyle.css';
import { Header } from '../../../../components/header/header';
import { ButtonIconStyled } from '../../../../views/devices/devicesStyles';
import {
  StyledHeaderContainer,
  StyledTitleHeader
} from '../../../../components/layout/main/mainStyles';

export const SportSettingsByLocation = () => {
  const [sportSettingsArray, setSportSettingsArray] = useState<ISportSetting[]>(
    []
  );
  const navigate = useNavigate();
  const [minTimeExecute, setMinTimeExecute] = useState(new Date());
  const [maxTimeExecute, setMaxTimeExecute] = useState(new Date());
  const [showSaveAlert, setShowSaveAlert] = useState(false);
  const [sportsObject, setSportsObject] = useState<
    Record<string, ISportSetting>
  >({});
  const { locationId } = useParams();

  const [
    getSportLocationSettings,
    {
      data: dataSportLocationSettings,
      isSuccess: isSuccessSportLocationSettings
    }
  ] = useGetSportLocationSettingsMutation();

  const [
    createSportLocationSettings,
    { isSuccess: isSuccessCreateSportLocationSettings }
  ] = useCreateSportLocationSettingsMutation();

  const [
    editSportLocationSettings,
    { isSuccess: isSuccessEditSportLocationSettings }
  ] = useEditSportLocationSettingsMutation();

  useEffect(() => {
    if (locationId) {
      getSportLocationSettings(Number(locationId));
    }
  }, [locationId]);

  const setMinMaxTimeExecute = () => {
    const startSetting = sportsObject['SEST'];
    const endSetting = sportsObject['SEET'];

    if (startSetting && endSetting) {
      const timeMin = startSetting.value.split(':');
      const timeMax = endSetting.value.split(':');

      const newMinTime = new Date();
      newMinTime.setHours(+timeMin[0], +timeMin[1], (+timeMin[2] || 0) + 1);
      setMinTimeExecute(newMinTime);

      const newMaxTime = new Date();
      newMaxTime.setHours(+timeMax[0], +timeMax[1], (+timeMax[2] || 0) - 1);
      setMaxTimeExecute(newMaxTime);
    }
  };

  useEffect(() => {
    if (isSuccessSportLocationSettings) {
      const newSportsObject = _.keyBy(
        dataSportLocationSettings?.data || [],
        'key'
      );
      setSportSettingsArray(dataSportLocationSettings?.data || []);
      setSportsObject(newSportsObject);

      setMinMaxTimeExecute();
    }
  }, [isSuccessSportLocationSettings]);

  const updateSportSetting = (
    updatedSetting: ISportSetting,
    editedSetting: any
  ) => {
    setSportSettingsArray((prevSettings) =>
      prevSettings.map((setting) =>
        setting.key === updatedSetting.key ? editedSetting : setting
      )
    );

    setSportsObject((prevObject) => ({
      ...prevObject,
      [updatedSetting.key]: editedSetting
    }));
  };

  const sendSportsSettings = async () => {
    const payload = sportSettingsArray.map(
      ({ value, valueType, sportLocationSettingTemplateId, id }) => ({
        value,
        valueType,
        locationId: Number(locationId),
        sportLocationSettingTemplateId,
        id
      })
    );
    if (payload.some((item) => item.id === 0)) {
      await createSportLocationSettings(payload);
    } else {
      await editSportLocationSettings(payload);
    }
  };

  useEffect(() => {
    if (
      isSuccessCreateSportLocationSettings ||
      isSuccessEditSportLocationSettings
    ) {
      getSportLocationSettings(Number(locationId));
      setShowSaveAlert(false);
      toast.success(Strings.sports.locationSettingsSaved);
    }
  }, [
    isSuccessCreateSportLocationSettings,
    isSuccessEditSportLocationSettings
  ]);

  return (
    <div>
      <Header>
        <StyledHeaderContainer>
          <div className="row justify-content-between">
            <div className="col-4 text-left">
              <ButtonIconStyled
                size="small"
                onClick={() =>
                  navigate(`/locations/${locationId}/applications/game_day`)
                }
              >
                <ArrowBackIos />
              </ButtonIconStyled>
            </div>
            <div className="col-4 text-center">
              <StyledTitleHeader>
                {Strings.sports.sportSettingsByLocation}
              </StyledTitleHeader>
            </div>
            <div className="col-4 text-right">
              <Tooltip title={Strings.common.save}>
                <ButtonIconStyled size="small" onClick={sendSportsSettings}>
                  <Save />
                </ButtonIconStyled>
              </Tooltip>
            </div>
          </div>
        </StyledHeaderContainer>
      </Header>
      <div>
        <p
          className="alert-warning text-center"
          style={{ opacity: showSaveAlert ? 1 : 0 }}
        >
          {Strings.sports.clickOnSaveToKeepYourChanges}
        </p>
        <div className="row align-items-center justify-content-center mt-2">
          <div className="col-11">
            <div className="p-0">
              <div className="row">
                {sportSettingsArray.map((item) => (
                  <div key={item.key} className="col-12 col-md-6 col-lg-4">
                    <CardSportSetting
                      sportSetting={item}
                      minTimeExecute={minTimeExecute}
                      maxTimeExecute={maxTimeExecute}
                      onResetMinMaxTime={setMinMaxTimeExecute}
                      onEdited={(isEdited) => {
                        setShowSaveAlert(isEdited);
                        updateSportSetting(item, isEdited);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
